import { z } from "zod";
import { ISODateSchema, ULIDSchema } from "./primitives.js";
import { UserIdSchema } from "./user.js";
import { newId } from "../ulid.js";
// ISO calendar date (YYYY-MM-DD), no time component. Distinct from ISODateSchema
// (which is a full UTC instant with Z). Used for weekStartDate / Day.date / Task dates.
export const CalendarDateSchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "must be an ISO calendar date (YYYY-MM-DD)");
export const MealTypeSchema = z.enum(["breakfast", "lunch", "dinner"]);
export const MEAL_TYPE_RANK = { breakfast: 0, lunch: 1, dinner: 2 };
export const TaskKindSchema = z.enum([
    "defrost", "marinate", "soak", "buy", "prep", "startBy", "other",
]);
export const SlotItemSchema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("recipe"), recipeId: ULIDSchema("rec") }).passthrough(),
    z
        .object({
        kind: z.literal("freetext"),
        title: z.string().min(1).max(200),
        reference: z.string().max(200).optional(),
    })
        .passthrough(),
]);
export const LeftoverRefSchema = z
    .object({
    slotId: ULIDSchema("slot").optional(),
    label: z.string().min(1).max(200).optional(),
})
    .passthrough()
    .refine((v) => v.slotId !== undefined || v.label !== undefined, "leftoverOf needs slotId or label");
export const SlotSchema = z
    .object({
    id: ULIDSchema("slot"),
    mealType: MealTypeSchema,
    items: z.array(SlotItemSchema).default([]),
    leftoverOf: LeftoverRefSchema.optional(),
    skipped: z.boolean().optional(),
    note: z.string().max(500).optional(),
    cookedAt: ISODateSchema.optional(), // set client-side at cook completion; absent = not cooked
})
    .passthrough();
export const DaySchema = z
    .object({
    date: CalendarDateSchema,
    slots: z.array(SlotSchema).default([]),
    note: z.string().max(1000).optional(),
})
    .passthrough();
export const TaskSchema = z
    .object({
    id: ULIDSchema("task"),
    doOnDate: CalendarDateSchema,
    title: z.string().min(1).max(200),
    kind: TaskKindSchema.optional(),
    forDate: CalendarDateSchema.optional(),
    startByTime: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "HH:MM").optional(),
    done: z.boolean().default(false),
    doneBy: UserIdSchema.optional(),
    doneAt: ISODateSchema.optional(),
})
    .passthrough();
const planCore = {
    schemaVersion: z.literal(1),
    weekStartDate: CalendarDateSchema,
    cookingFor: z.number().int().positive().max(99).optional(), // recorded, unused in A
    days: z.array(DaySchema).default([]),
    tasks: z.array(TaskSchema).default([]),
    createdAt: ISODateSchema,
    updatedAt: ISODateSchema,
    createdBy: UserIdSchema,
    updatedBy: UserIdSchema,
    deletedAt: ISODateSchema.nullable(),
};
export const MealPlanRowSchema = z
    .object({ householdId: ULIDSchema("hh"), sk: CalendarDateSchema, ...planCore })
    .passthrough();
export const MealPlanApiItemSchema = z.object({ ...planCore }).passthrough();
export function mealPlanRowToApiItem(raw) {
    const row = MealPlanRowSchema.parse(raw);
    const merged = { ...row };
    delete merged["householdId"];
    delete merged["sk"];
    delete merged["ttl"];
    return MealPlanApiItemSchema.parse(merged);
}
// POST body: either a bare { weekStart } (server seeds an empty skeleton) or a full
// composed week (duplicate helper, §4.7). days/tasks optional here; server fills defaults.
export const CreateMealPlanRequestSchema = z
    .object({
    weekStart: CalendarDateSchema,
    days: z.array(DaySchema).max(7).optional(),
    tasks: z.array(TaskSchema).max(200).optional(),
    cookingFor: z.number().int().positive().max(99).optional(),
})
    .strict();
export const PatchMealPlanRequestSchema = z
    .object({
    days: z.array(DaySchema).max(7).optional(),
    tasks: z.array(TaskSchema).max(200).optional(),
    cookingFor: z.number().int().positive().max(99).nullable().optional(),
})
    .strict()
    .refine((v) => v.days !== undefined || v.tasks !== undefined || v.cookingFor !== undefined, "at least one of days, tasks, cookingFor is required");
export const MealPlanResponseSchema = z.object({ plan: MealPlanApiItemSchema });
export const MealPlansResponseSchema = z.object({
    items: z.array(MealPlanApiItemSchema),
    nextCursor: z.string().nullable().optional(),
});
export function addCalendarDays(isoDate, n) {
    const [y, m, d] = isoDate.split("-").map((v) => Number.parseInt(v, 10));
    const dt = new Date(Date.UTC(y, m - 1, d, 12));
    dt.setUTCDate(dt.getUTCDate() + n);
    return dt.toISOString().slice(0, 10);
}
const MEAL_TYPES = ["breakfast", "lunch", "dinner"];
/** An empty 7-day / 21-slot week skeleton with fresh slot ids. */
export function buildEmptyWeek(weekStart, userId, now) {
    const days = [];
    for (let i = 0; i < 7; i++) {
        const date = addCalendarDays(weekStart, i);
        days.push({
            date,
            slots: MEAL_TYPES.map((mealType) => ({ id: newId("slot"), mealType, items: [] })),
        });
    }
    return {
        schemaVersion: 1,
        weekStartDate: weekStart,
        days,
        tasks: [],
        createdAt: now,
        updatedAt: now,
        createdBy: userId,
        updatedBy: userId,
        deletedAt: null,
    };
}
//# sourceMappingURL=meal-plan.js.map